/**
 * Component: Widget API Error Shapes
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R06, WIDGET-V1-R08, WIDGET-V1-R20
 *
 * Documented error taxonomy surfaced by the widget's HTTP
 * client and init pipeline. Every error a host-site
 * integrator might observe via the onError callback is a
 * named subclass of ``WidgetError`` so consumers can
 * pattern-match on ``instanceof`` rather than parsing error
 * message strings.
 *
 * Keeping the taxonomy in a single file makes the
 * integration-guide deliverable (WIDGET-V1-R24) smaller —
 * the docs can point at this file for the canonical list
 * without enumerating error codes across the codebase.
 */

/**
 * Base class for every documented widget error. Host
 * ``onError`` callbacks always receive an instance (or
 * subclass) of this type.
 */
export class WidgetError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WidgetError';
  }
}

/**
 * Raised when the backend returns a non-2xx status the
 * widget did not handle specially (i.e. not 401, not
 * version mismatch). Carries the HTTP status code and the
 * response body (when available) so hosts can log or
 * retry.
 */
export class WidgetApiError extends WidgetError {
  readonly status: number;
  readonly body: string | null;

  constructor(message: string, status: number, body: string | null) {
    super(message);
    this.name = 'WidgetApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Raised when authentication failed unrecoverably — either
 * the host did not provide an ``onAuthRefresh`` callback to
 * re-mint the JWT, or the refresh itself failed, or the
 * second (post-refresh) attempt also returned 401.
 *
 * WIDGET-V1-R08 requires the widget surface this so the
 * host page can react (sign the user out, prompt re-auth,
 * etc.) without needing to inspect internal state.
 */
export class WidgetAuthError extends WidgetError {
  constructor(message: string) {
    super(message);
    this.name = 'WidgetAuthError';
  }
}

/**
 * Raised when the backend is unreachable (network failure,
 * DNS failure, CORS rejection, timeout). WIDGET-V1-R20
 * degrades gracefully in this case by rendering the
 * documented error state inside the widget container and
 * invoking the host error callback.
 */
export class WidgetNetworkError extends WidgetError {
  readonly cause: unknown;

  constructor(message: string, cause: unknown) {
    super(message);
    this.name = 'WidgetNetworkError';
    this.cause = cause;
  }
}

/**
 * Raised when the backend returns HTTP 429 Too Many
 * Requests. Carries the parsed ``Retry-After`` header so
 * the host can either display a user-visible countdown or
 * schedule a follow-up call after the server-advised delay.
 *
 * WIDGET-V1-R19 requires the widget surface rate-limit
 * responses to host callbacks "with retry-after metadata"
 * and display a user-visible rate-limit message. The
 * typed error here is the metadata channel; the surface
 * components render the user-visible message themselves.
 */
export class WidgetRateLimitError extends WidgetError {
  /**
   * Parsed ``Retry-After`` header in seconds. The header
   * may arrive as an integer seconds value or as an HTTP
   * date; ``null`` when the header was missing or
   * unparseable. Consumers typically either sleep for the
   * advised duration or show a "try again in N seconds"
   * message.
   */
  readonly retryAfterSeconds: number | null;

  constructor(message: string, retryAfterSeconds: number | null) {
    super(message);
    this.name = 'WidgetRateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Parse an HTTP ``Retry-After`` header value into seconds.
 * The spec allows either an integer number of seconds or
 * an RFC 7231 HTTP date. Returns ``null`` when the header
 * is absent or cannot be parsed.
 *
 * Exported from the errors module so tests and
 * ``WidgetApiClient`` / ``streamPostJson`` share a single
 * implementation.
 */
export function parseRetryAfterHeader(raw: string | null): number | null {
  if (raw === null) return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  // Integer seconds form.
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number.parseInt(trimmed, 10);
    return Number.isFinite(seconds) ? seconds : null;
  }
  // HTTP date form. Date.parse returns NaN for
  // unparseable strings, which we normalise to null.
  const targetMs = Date.parse(trimmed);
  if (Number.isNaN(targetMs)) return null;
  const nowMs = Date.now();
  const seconds = Math.max(0, Math.round((targetMs - nowMs) / 1000));
  return seconds;
}
